<template>
  <li class="menu-item" :class="{ 'menu-item--has-children': hasChildren }">
    <!-- Cabecera del ítem -->
    <div
      class="menu-item__header"
      :class="{ clickable: hasChildren, 'menu-item__header--active': isOpen }"
      @click="toggle"
      ref="header"
    >
      <font-awesome-icon :icon="item.icon || 'folder-open'" class="fixed-icon-size" />
      <span class="menu-item-text">{{ item.title }}</span>
      <font-awesome-icon
        v-if="hasChildren"
        :icon="isOpen ? 'angle-down' : 'angle-right'"
        class="dropdown-icon"
      />
    </div>

    <!-- Submenú recursivo -->
    <template v-if="hasChildren && isOpen">
      <!-- Nivel 1: Flotante a la derecha del sidebar -->
      <teleport to="body" v-if="depth === 0">
        <div
          class="floating-submenu-wrapper"
          :style="floatingStyle"
          ref="submenu"
          @mouseenter="onSubmenuEnter"
          @mouseleave="onSubmenuLeave"
          @click.stop
        >
          <!-- Título del grupo -->
          <div class="floating-submenu-header">
            <font-awesome-icon :icon="item.icon || 'folder-open'" class="floating-header-icon" />
            <span>{{ item.title }}</span>
          </div>
          <div class="floating-divider"></div>
          <ul class="sub-menu-list">
            <MenuNestedItem
              v-for="child in subItems"
              :key="child.title"
              :item="child"
              :depth="depth + 1"
              :isMenuCollapsed="isMenuCollapsed"
              @navigate="handleNavigate"
            />
          </ul>
        </div>
      </teleport>

      <!-- Nivel 2 en adelante: Acordeón hacia abajo dentro del panel flotante -->
      <transition name="nested-submenu" v-else>
        <ul class="sub-menu-list nested-sub-menu">
          <MenuNestedItem
            v-for="child in subItems"
            :key="child.title"
            :item="child"
            :depth="depth + 1"
            :isMenuCollapsed="isMenuCollapsed"
            @navigate="$emit('navigate', $event)"
          />
        </ul>
      </transition>
    </template>
  </li>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';


export default {
  name: 'MenuNestedItem',
  components: { FontAwesomeIcon },
  props: {
    item: { type: Object, required: true },
    depth: { type: Number, default: 0 },
    isMenuCollapsed: { type: Boolean, default: false },
  },
  emits: ['navigate'],
  data() {
    return {
      isOpen: false,
      floatingStyle: {}
    };
  },
  computed: {
    hasChildren() {
      const subs = this.item.subMenu || this.item.children;
      return subs && subs.length > 0;
    },
    subItems() {
      return this.item.subMenu || this.item.children || [];
    }
  },
  watch: {
    isOpen(newValue) {
      if (newValue && this.depth === 0) {
        // Notificar a los demás ítems raíz que se cierren
        window.dispatchEvent(new CustomEvent('menu-root-opened', { detail: { title: this.item.title } }));
        document.addEventListener('click', this.handleOutsideClick);
      } else {
        document.removeEventListener('click', this.handleOutsideClick);
      }
    },
    isMenuCollapsed(newValue) {
      if (newValue) {
        this.isOpen = false;
      }
    }
  },
  mounted() {
    if (this.depth === 0) {
      window.addEventListener('menu-root-opened', this.onOtherMenuOpened);
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
    window.removeEventListener('menu-root-opened', this.onOtherMenuOpened);
  },
  methods: {
    toggle(event) {
      if (event) event.stopPropagation();
      if (this.hasChildren) {
        this.isOpen = !this.isOpen;
        if (this.isOpen && this.depth === 0) {
          this.$nextTick(() => {
            const rect = this.$refs.header.getBoundingClientRect();
            const sidebarRight = 15 + 280 + 8;  // 303px en desktop
            const availableRight = window.innerWidth - sidebarRight - 8;

            let style;

            if (availableRight < 200) {
              // MÓVIL: no hay espacio a la derecha → posicionar debajo del ítem
              const panelW = Math.min(320, window.innerWidth - 16);
              const leftPos = Math.max(8, (window.innerWidth - panelW) / 2);
              const top = Math.min(rect.bottom + 4, window.innerHeight - 120);
              const maxHeight = Math.max(150, window.innerHeight - top - 16);
              style = {
                position: 'fixed',
                top: `${top}px`,
                left: `${leftPos}px`,
                width: `${panelW}px`,
                maxWidth: 'none',
                maxHeight: `${maxHeight}px`,
                zIndex: 1050,
              };
            } else {
              // DESKTOP: a la derecha del sidebar
              const panelW = Math.min(260, availableRight);
              const top = Math.max(12, Math.min(rect.top, window.innerHeight - 120));
              const maxHeight = Math.max(150, window.innerHeight - top - 16);
              style = {
                position: 'fixed',
                top: `${top}px`,
                left: `${sidebarRight}px`,
                width: `${panelW}px`,
                maxHeight: `${maxHeight}px`,
                zIndex: 1050,
              };
            }

            this.floatingStyle = style;
          });
        }
      } else {
        this.$emit('navigate', this.item.route);
      }
    },
    handleNavigate(route) {
      this.isOpen = false;
      this.$emit('navigate', route);
    },
    handleOutsideClick(event) {
      const header = this.$refs.header;
      const submenu = this.$refs.submenu;
      if (
        (header && !header.contains(event.target)) &&
        (!submenu || !submenu.contains(event.target))
      ) {
        this.isOpen = false;
      }
    },
    onOtherMenuOpened(event) {
      // Si otro ítem raíz se abrió, cerrar éste
      if (event.detail.title !== this.item.title) {
        this.isOpen = false;
      }
    },
    onSubmenuEnter() {
      if (this.depth === 0) window.isHoveringSubmenu = true;
    },
    onSubmenuLeave() {
      if (this.depth === 0) {
        window.isHoveringSubmenu = false;
        window.dispatchEvent(new Event('submenu-mouse-leave'));
      }
    }
  },
};
</script>

<style scoped>
/* ── Base item ─────────────────────────────────────────────── */
.menu-item {
  list-style: none;
  width: 100%;
}

.menu-item__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 12px;
  border-radius: 8px;
  cursor: default;
  min-height: 34px;
  transition: background-color 0.2s, color 0.2s;
  position: relative;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-color);
  letter-spacing: 0.01em;
}

.menu-item__header.clickable {
  cursor: pointer;
}

.menu-item__header:hover,
.menu-item__header--active {
  background-color: var(--menu-hover-background-color, rgba(116, 168, 11, 0.11));
  color: var(--primary-color);
}

.menu-item-text {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-icon {
  font-size: 0.7rem;
  transition: transform 0.2s;
  flex-shrink: 0;
  opacity: 0.6;
}

.fixed-icon-size {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  opacity: 0.8;
}

/* ── Flotante del nivel 0 ──────────────────────────────────── */
.floating-submenu-wrapper {
  font-family: var(--font-body, 'Inter', sans-serif);
  background-color: var(--card-background-color, #ffffff);
  color: var(--text-color, #2d2d2d);
  border: 1px solid var(--border-color, #e8e8e8);
  border-radius: 14px;
  box-shadow:
    0 4px 6px rgba(0, 0, 0, 0.04),
    0 12px 32px rgba(0, 0, 0, 0.12);
  padding: 10px 8px;
  min-width: 230px;
  max-width: 280px;
  max-height: 75vh;
  overflow-y: auto;
  animation: floatIn 0.18s cubic-bezier(0.25, 0.8, 0.25, 1);

  /* scrollbar estilizado */
  scrollbar-width: thin;
  scrollbar-color: rgba(116, 168, 11, 0.3) transparent;

  /* propagar variables a hijos teleported */
  --menu-hover-background-color: rgba(116, 168, 11, 0.1);
  --menu-hover-text-color: var(--primary-color);
  --submenu-background-color: var(--card-background-color, #ffffff);
}

.floating-submenu-wrapper::-webkit-scrollbar {
  width: 4px;
}
.floating-submenu-wrapper::-webkit-scrollbar-track {
  background: transparent;
}
.floating-submenu-wrapper::-webkit-scrollbar-thumb {
  background-color: rgba(116, 168, 11, 0.3);
  border-radius: 4px;
}

/* Título del grupo */
.floating-submenu-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px 6px 10px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--primary-color);
  opacity: 0.85;
}

.floating-header-icon {
  width: 12px;
  height: 12px;
  opacity: 0.7;
}

.floating-divider {
  height: 1px;
  background: var(--border-color, #e8e8e8);
  margin: 0 6px 6px 6px;
  border-radius: 1px;
}

/* ── Listas ─────────────────────────────────────────────────── */
.sub-menu-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

/* Submenu anidado acordeón */
.nested-sub-menu {
  padding: 0;
  margin: 0;
  padding-left: 10px;
  border-left: 2px solid var(--border-color, rgba(116, 168, 11, 0.2));
  margin-left: 16px;
  margin-top: 2px;
  margin-bottom: 2px;
}

/* ── Animaciones ────────────────────────────────────────────── */
.nested-submenu-enter-active,
.nested-submenu-leave-active {
  transition: max-height 0.28s ease, opacity 0.18s ease;
  overflow: hidden;
  max-height: 600px;
}
.nested-submenu-enter-from,
.nested-submenu-leave-to {
  max-height: 0;
  opacity: 0;
}

@keyframes floatIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
